import { useId } from 'react'

import { cx } from '../atoms/cx.ts'

import { chamberScene, type ChamberDetail, type ChamberPhase } from './chamber3d/chamberScene.ts'
import type { ChamberFigureState } from './ChamberFigure.tsx'
import {
  chamberFrontBar,
  chamberFrontBody,
  chamberFrontController,
  chamberFrontInside,
  chamberFrontShake,
  chamberFrontSpeed,
  chamberFrontTool,
} from './chamberFront.ts'
import type { ChamberToolDrawing } from './chamberOutline.ts'
import { chamberStageLook, type ChamberStageProgress } from './chamberStageLook.ts'
import { TRAY_CELL_H, TRAY_CELL_W } from './TrayFigure.tsx'

import './figures.css'

/**
 * 챔버 한 개 — **통합 콘솔 2D · 하이픽(HypICC) 챔버를 정면(옆 단면)에서 본 그림**
 * (eric 2026-10-08 「장비 쪽 많이 진행했잖아.. 그거 바탕으로 시료 쪽 챔버 그림 만들어줘」 → 「그럼.. 정면도로 바꾸자」).
 *
 * 🔴 **시점은 정면이다 — eric 이 2026-10-08 에 바꿨다.** 장비 판(원심 그리퍼 · 어셈블러 그리퍼 + 압착판 + 아래 UV · 염색기 피펫)이
 *    전부 정면이라 같은 시점으로 선다. ⚠ 2026-09-29 의 「바로 위에서 본 평면도」는 이 결정으로 걷혔다 — 되살리지 마라.
 * 🔴 정면에서는 **층**이 보인다(`chamberFront.ts`) — 바닥 젤 층 · 세포층(원심이 가라앉힌 것) · 젤 막(UV 가 굳힌 것) · 액체.
 *    받침판 → 몸통(벽) → 우물이고, 도구는 장비 판과 같은 말이다(원심 = 속도선 + 아래 화살표 · 어셈블러 = 조절기 + 누름 · UV = 아래에서
 *    드는 빛 · 염색 = 위에서 드는 피펫 팁).
 * 🔴 **무엇을 그릴지는 3D 와 같은 표**가 정한다 — `chamberScene()`(우물 안) · `chamberTool()`(단계 → 도구).
 * 🔴 **몸 · 윤곽은 흑색 · 중성이고, 테마(강조)색은 안의 것 — 액체 · 피펫 팁 · 회전 표지에만이다**(eric 2026-10-14
 *    「다 파란색이니.. 구분이 안가.. 바깥쪽은 검정색으로 하고. 안의 액체랑.. 팁 같은 것만 다른 색」 — 2026-10-12 의 「몸까지 테마색」을
 *    걷었다). 색은 전부 `figures.css` `.fig-cs` 의 토큰이 쥔다 — 이 파일은 색을 모른다.
 *    젤 층 · 세포층 · 막 · 조절기는 중성, 꺼진 챔버(중단 · 폐기 · 배정 모름)는 한 단 더 옅다. 위험색(`--color-danger`)은 실패에만.
 *    물건 색(`chamberPalette.ts`)을 읽지 않는다.
 * 🔴 **몸통 윤곽이 상태를 진다**(점선 · 실선 · 굵기 · 숨쉬기 — `chamberStageLook`). 진행 강조는 색이 아니라 굵기다.
 * 🔴 **세포 원이 없다**(eric 2026-09-30 「암세포인가로 보이는.. 동그라미 세개 없애줘」) — 원은 방울 · 스포이드 머리뿐이다.
 *    크고 짙게 채운 원도 없다(eric 「사각형 안에.. 동그라미 검정색은 왜 넣은 거야?」) — 채움은 옅은 단이거나 테뿐이다.
 * 🔴 **viewBox 는 72×50** — 3D 자리(`VIEW_W`×`VIEW_H`)와 같은 틀이라 카드가 안 흔들린다.
 * ⚠ 선 굵기는 **화면 픽셀**이다(`vector-effect: non-scaling-stroke`) — 카드와 서랍에서 같은 굵기로 선다.
 * ⚠ 모션은 「진행」에서만, 토큰(`--dur-pulse`)이 쥔다 — `prefers-reduced-motion` 에서 0ms 로 떨어지면 멈춰 선다.
 */
export interface ChamberStageFigureProps {
  state: ChamberFigureState
  /** 배치 표지가 말하는 공정 자리. 없으면 단계 모습 없이 상태만(빈 우물) */
  phase?: ChamberPhase | undefined
  /** 모듈 세부 국면 · 그 웰의 분주 기록(서버 사실) — `chamberTool()` 이 읽는다 */
  detail?: ChamberDetail | undefined
  /** 챔버 진행 4종(`chamberProgress().status`). 몸통 윤곽을 정한다 */
  progress?: ChamberStageProgress | undefined
}

const W = TRAY_CELL_W
const H = TRAY_CELL_H

/** 몸 — 상태와 무관하다(실물 하드웨어는 늘 같다). 한 번만 셈한다 */
let outline: ReturnType<typeof chamberFrontBody> | undefined
const bodyOutline = () => (outline ??= chamberFrontBody())

/** 해칭 한 칸(그림 단위) */
const HATCH = 3

/**
 * 🔴 **그림은 `aria-hidden` 이다.** 스크린리더가 읽는 것은 부르는 쪽의 문구다(`BundleChamber` 의 `.a-sr`).
 */
export function ChamberStageFigure({ state, phase, detail, progress }: ChamberStageFigureProps) {
  const uid = useId()
  const clipId = `${uid}-body`
  const hatchId = `${uid}-hatch`
  const look = chamberStageLook(state, progress)
  /*
   * 🔴 진행 어휘가 「실패」면 장면도 실패로 읽는다 — `chamberTool()` 이 실패에는 도구를 안 얹는다
   *    (도구가 돌면 「아직 한다」로 읽힌다). 그 밖에는 받은 상태 그대로다.
   */
  const model = chamberScene(look === 'fail' ? 'fail' : state, phase, detail)
  /*
   * 🔴 우물 안을 그리는 것은 **단계를 알 때만**이다. 단계를 모르면 빈 우물 — 상태 6종의 채움을 지어 넣지 않는다.
   * 🔴 배정 모름도 비운다 — 누구 것인지 모르는 챔버에 공정을 그리면 아는 척이 된다.
   */
  const inside = look !== 'unassigned' && phase !== undefined
  const body = bodyOutline()
  const content = inside ? chamberFrontInside(model) : null
  /*
   * 🔴 **「단계에 와 있음」(`stage`)에는 도구 · 조절기를 얹지 않는다**(eric 2026-10-06 · `chamberStageLook.ts` 표).
   *    도구(회전 · 누름 · 스포이드)는 「이 챔버가 지금 그 일을 받고 있다」는 말이라, 챔버별 진행을 모르는 구간에 아홉 칸이
   *    함께 얹으면 그 순간 안 도는 챔버까지 움직인다. 우물 안(그 단계의 모습)은 그대로 — 「그 단계에 와 있다」는 사실이다.
   */
  const atStage = look === 'stage'
  /* 🔴 두께 조절기 — 어셈블러 진행에서 얹힌다(3D 와 같은 `model.controller`) */
  const controller = inside && !atStage && model.controller ? chamberFrontController() : null
  /*
   * 🔴 스포이드는 «이 웰에 지금 피펫이 있다»는 웰 하나의 서버 사실이라(`detail.pipetteHere`) 「단계에 와 있음」에도 선다 —
   *    모듈 근거로 아홉 칸이 같이 얹는 도구가 아니다(eric 2026-10-12 「분주할 때.. 피펫 넣는 거 표현해줘」)
   */
  const pinned = inside && model.tool === 'pipette'
  const tool = inside && (!atStage || pinned) ? chamberFrontTool(model.tool) : null
  const hasTool = tool !== null && model.tool !== 'none'
  /*
   * 🔴 **회전 중에는 챔버 자체가 바뀐다**(eric 2026-10-12 「원심분리기 회전할 때.. 시료 화면의 챔버 확실하게 변환해줘」) — 몸(`__rig`)이 로터 궤도를
   *    따라 작은 원을 돌고(제자리 회전이 아니다 · 실물은 로터 축을 돈다), 양옆에 흐르는 속도선 · 우물 안 굵은 화살표가 선다.
   *    조건은 도구와 같다 — 서버가 그 챔버를 가리켜 「진행」이고 모듈이 `RUNNING`(정지 아님)일 때만(`chamberTool` · `runningChamberStep`)
   */
  const spinning = hasTool && model.tool === 'spin'
  /*
   * 흔들기 — 염색 진행 중 모듈 세부가 `rest`(흔들기 · 배양)라고 말할 때만. 🔴 도구가 아니다(`chamberTool()` 은 `none`) —
   * 서버 사실(`detail.step`)이 그렇다고 할 때만 서고, 실패 · 중단에는 안 선다.
   */
  const shaking =
    inside &&
    !atStage &&
    look !== 'fail' &&
    look !== 'halted' &&
    phase.stage === 'staining' &&
    !phase.done &&
    detail?.step?.stage === 'staining' &&
    detail.step.step === 'rest'

  return (
    <svg
      aria-hidden="true"
      className={cx('fig', 'fig-cs', `fig-cs--${look}`, spinning && 'fig-cs--spin')}
      data-look={look}
      data-stage={phase?.stage}
      data-tool={atStage && !pinned ? 'none' : model.tool}
      data-view="front"
      focusable="false"
      viewBox={`0 0 ${String(W)} ${String(H)}`}
    >
      <defs>
        <clipPath id={clipId}>
          <path d={body.well} />
        </clipPath>
        <pattern
          height={HATCH}
          id={hatchId}
          patternTransform="rotate(45)"
          patternUnits="userSpaceOnUse"
          width={HATCH}
        >
          <line className="fig-cs__hatchline" x1={0} x2={0} y1={0} y2={HATCH} />
        </pattern>
      </defs>

      {spinning ? (
        <g className="fig-cs__speed">
          {chamberFrontSpeed().map((d) => (
            <path className="fig-cs__speedline" d={d} key={d} />
          ))}
        </g>
      ) : null}
      {/* 몸 한 덩어리 — 회전 중에는 이 묶음이 궤도를 돈다(`fig-cs--spin`) */}
      <g className="fig-cs__rig">
        {/* 받침(플랜지) · 어깨 둘(곡면) — 가장 옅은 면 · 실물 하이픽 챔버 실루엣(eric 2026-10-12 · `chamberFront.ts`) */}
        <path className="fig-cs__plate" d={body.plate} />
        {body.feet.map((d) => (
          <path className="fig-cs__rib" d={d} key={d} />
        ))}

        {/* 우물(속) — 한 단 짙은 면 · 벽 — 몸통에서 우물을 뺀 띠(위가 트였다) */}
        <path className="fig-cs__hole" d={body.well} />
        <path className="fig-cs__wall" d={body.wall} fillRule="evenodd" />

        {/* 우물 안 — 젤 층 · 채움 · 막 */}
        {content?.bed ? (
          <path
            className={cx(
              'fig-cs__bed',
              /* 젤 층만 — 등록. 테를 둘러 「단계 모름」(빈 우물)과 가른다 */
              content.liquid === null && content.film === null && 'fig-cs__bed--only',
            )}
            d={content.bed}
          />
        ) : null}
        {content?.liquid ? (
          <path
            className={cx('fig-cs__liquid', `fig-cs__liquid--${content.liquid.level}`)}
            d={content.liquid.d}
          />
        ) : null}
        {/* 세포층 — 원심 완료(젤 층 위에 가라앉았다). 🔴 원이 아니라 안쪽 면 + 테 */}
        {content?.sediment ? <path className="fig-cs__sediment" d={content.sediment} /> : null}
        {content?.film ? <path className="fig-cs__film" d={content.film} /> : null}

        {/* 몸통 — 🔴 겉 윤곽이 상태를 진다 · 입구 선 */}
        <path className="fig-cs__shell" d={body.shell} />
        <path className="fig-cs__opening" d={body.opening} />

        {look === 'fail' ? (
          <rect
            className="fig-cs__hatch"
            clipPath={`url(#${clipId})`}
            fill={`url(#${hatchId})`}
            height={H}
            width={W}
            x={0}
            y={0}
          />
        ) : null}
        {look === 'halted' ? <path className="fig-cs__bar" d={chamberFrontBar()} /> : null}

        {controller === null ? null : (
          <g
            className={cx(
              'fig-cs__controller',
              model.tool === 'press' && 'fig-cs__tool fig-cs__tool--press',
            )}
          >
            <path className="fig-cs__ctrl" d={controller.frame} fillRule="evenodd" />
            <path className="fig-cs__ctrl" d={controller.handle} />
            {model.tool === 'press' && tool !== null ? <ToolMarks tool={tool} /> : null}
          </g>
        )}
        {hasTool && model.tool !== 'press' ? (
          <g className={cx('fig-cs__tool', `fig-cs__tool--${model.tool}`)}>
            <ToolMarks tool={tool} />
          </g>
        ) : null}
      </g>
      {shaking ? (
        <g className="fig-cs__shake">
          {chamberFrontShake().map((d) => (
            <path className="fig-cs__line" d={d} key={d} />
          ))}
        </g>
      ) : null}
    </svg>
  )
}

/** 도구의 선 · 도형 · 점 */
function ToolMarks({ tool }: { tool: ChamberToolDrawing }) {
  return (
    <>
      {tool.shapes.map((d) => (
        <path className="fig-cs__toolshape" d={d} key={d} />
      ))}
      {tool.ripples.map((d) => (
        <path className="fig-cs__ripple" d={d} key={d} />
      ))}
      {tool.lines.map((d) => (
        <path className="fig-cs__line" d={d} key={d} />
      ))}
      {tool.bulbs.map((c) => (
        <circle
          className="fig-cs__bulb"
          cx={c.x}
          cy={c.y}
          key={`${String(c.x)}-${String(c.y)}`}
          r={c.r}
        />
      ))}
      {tool.dots.map((c) => (
        <circle
          className="fig-cs__drop"
          cx={c.x}
          cy={c.y}
          key={`${String(c.x)}-${String(c.y)}`}
          r={c.r}
        />
      ))}
    </>
  )
}
